import { z } from "zod";
import { incomingRequestMessageSchema, isoTimestampSchema, pairingClosedMessageSchema, pairingCreatedMessageSchema, pinChangedMessageSchema, pongMessageSchema, requestResolvedMessageSchema, serverErrorMessageSchema, } from "./throw-protocol.js";
/**
 * Vertrag fuer den Dateiaustausch innerhalb einer Kopplung.
 *
 * Grundsatz: Metadaten laufen ueber JSON und WebSocket, Dateiinhalte
 * ausschliesslich ueber eigene HTTP-Endpunkte als Datenstrom. Kein Byte einer
 * Nutzdatei geht jemals durch den JSON-Parser oder ueber den WebSocket.
 */
/**
 * Lebenslauf einer Datei.
 *
 * - `offered`    Der Sender hat die Datei angekuendigt, der Empfaenger hat noch
 *                nicht entschieden. Es sind noch keine Bytes geflossen.
 * - `accepted`   Der Empfaenger hat zugestimmt, der Upload darf beginnen.
 * - `uploading`  Bytes fliessen.
 * - `verifying`  Alle Bytes sind da, die Pruefsumme wird berechnet.
 * - `complete`   Pruefsumme steht, der Download ist freigegeben.
 * - `rejected`   Der Empfaenger hat das Angebot abgelehnt.
 * - `cancelled`  Der Sender hat abgebrochen.
 * - `failed`     Technischer Fehlschlag.
 * - `expired`    Aufbewahrungszeit abgelaufen, Datei geloescht.
 */
export const transferFileStatusSchema = z.enum([
    "offered",
    "accepted",
    "uploading",
    "verifying",
    "complete",
    "rejected",
    "cancelled",
    "failed",
    "expired",
]);
/** Zustaende, in denen die Datei nicht mehr weiterlaeuft. */
export const TERMINAL_FILE_STATUSES = [
    "complete",
    "rejected",
    "cancelled",
    "failed",
    "expired",
];
export const transferFileSchema = z.object({
    fileId: z.string(),
    /** "outgoing" = diese Sitzung sendet, "incoming" = diese Sitzung empfaengt. */
    direction: z.enum(["outgoing", "incoming"]),
    name: z.string(),
    size: z.number().int().nonnegative(),
    mimeType: z.string().nullable(),
    status: transferFileStatusSchema,
    /** Serverseitig tatsaechlich empfangene Bytes -- Grundlage fuer Fortschritt und Wiederaufnahme. */
    receivedBytes: z.number().int().nonnegative(),
    /** Erst nach `verifying` gesetzt. */
    sha256: z.string().nullable(),
    createdAt: isoTimestampSchema,
    expiresAt: isoTimestampSchema,
});
// --- REST: Anfragen ----------------------------------------------------------
/**
 * Dateiname-Regeln.
 *
 * Der Name wird nur als Metadatum gefuehrt und niemals als Pfad verwendet --
 * auf der Platte liegt die Datei unter einem zufaelligen Namen. Trotzdem wird
 * er hier streng geprueft, weil er beim Empfaenger in einem Dialog zum
 * Speichern landet und dort sehr wohl zu einem Pfad wird.
 */
export const fileNameSchema = z
    .string()
    .min(1, "Dateiname fehlt")
    .max(255, "Dateiname zu lang")
    .refine((name) => !name.includes("/") && !name.includes("\\"), "Dateiname darf keine Pfadtrenner enthalten")
    .refine((name) => !name.includes("\0"), "Dateiname darf keine Null-Bytes enthalten")
    .refine((name) => name !== "." && name !== "..", "Ungültiger Dateiname");
export const offerFileSchema = z.object({
    name: fileNameSchema,
    size: z.number().int().nonnegative(),
    mimeType: z.string().max(255).nullable().optional(),
    lastModified: z.number().int().nonnegative().nullable().optional(),
});
// --- REST: Antworten ---------------------------------------------------------
export const uploadTicketSchema = z.object({
    uploadId: z.string(),
    /** Ab diesem Byte muss weitergesendet werden -- nach einem Abbruch > 0. */
    offset: z.number().int().nonnegative(),
    size: z.number().int().nonnegative(),
    /** Vom Server empfohlene Chunkgroesse in Bytes. */
    chunkSize: z.number().int().positive(),
    expiresAt: isoTimestampSchema,
});
export const chunkResultSchema = z.object({
    offset: z.number().int().nonnegative(),
    status: transferFileStatusSchema,
});
// --- WebSocket ---------------------------------------------------------------
/** Ein neues Dateiangebot fuer den Empfaenger. */
export const fileOfferedMessageSchema = z.object({
    type: z.literal("file.offered"),
    file: transferFileSchema,
});
/** Zustandsaenderung einer Datei. Geht an beide Seiten der Kopplung. */
export const fileStatusMessageSchema = z.object({
    type: z.literal("file.status"),
    fileId: z.string(),
    status: transferFileStatusSchema,
    receivedBytes: z.number().int().nonnegative(),
    sha256: z.string().nullable(),
});
export const transferMessageSchema = z.discriminatedUnion("type", [
    fileOfferedMessageSchema,
    fileStatusMessageSchema,
]);
// --- HTTP-Header des Chunk-Protokolls ---------------------------------------
/**
 * Eigene Header statt Standardheader.
 *
 * `Content-Range` waere naheliegend, wird aber von Proxys teils umgeschrieben
 * und ist fuer Uploads nicht eindeutig spezifiziert. Ein eigener, klar
 * benannter Header laesst keinen Interpretationsspielraum.
 */
export const UPLOAD_OFFSET_HEADER = "upload-offset";
export const UPLOAD_LENGTH_HEADER = "upload-length";
export const CHUNK_CONTENT_TYPE = "application/offset+octet-stream";
// --- Vollstaendige Union aller Servernachrichten ------------------------------
/**
 * Jede Nachricht, die der Server ueber den WebSocket senden kann.
 *
 * Bewusst eine einzige Union statt mehrerer: der Client parst jede eingehende
 * Nachricht genau einmal gegen ein Schema und bekommt dabei einen erschoepfenden
 * Typ. Ergaenzt jemand eine Nachrichtenart und vergisst die Behandlung, meldet
 * das der Compiler.
 */
export const serverMessageSchema = z.discriminatedUnion("type", [
    incomingRequestMessageSchema,
    requestResolvedMessageSchema,
    pairingCreatedMessageSchema,
    pairingClosedMessageSchema,
    pinChangedMessageSchema,
    serverErrorMessageSchema,
    pongMessageSchema,
    fileOfferedMessageSchema,
    fileStatusMessageSchema,
]);
//# sourceMappingURL=transfer-protocol.js.map